import { useEffect, useRef, useState } from "react";
import { TrendingUp } from "lucide-react";
import { api, type TrendEntry } from "@/api/client";
import { useBrandsStore } from "@/stores/brands";
import { usePromptsStore } from "@/stores/prompts";
import { useModelsStore } from "@/stores/models";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { formatModelLabel } from "@/lib/format";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export function TrendsPanel() {
  const { brands, fetch: fetchBrands } = useBrandsStore();
  const { prompts, fetch: fetchPrompts } = usePromptsStore();
  const { models, fetch: fetchModels } = useModelsStore();

  const [brandId, setBrandId] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [promptFilter, setPromptFilter] = useState("");
  const [timeline, setTimeline] = useState<TrendEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonically increasing request id. Each fetch captures its sequence number;
  // only the most recent in-flight request is allowed to commit its result, so a
  // stale/out-of-order response (e.g. a slow request for a previous filter) is
  // dropped instead of clobbering newer data.
  const requestSeq = useRef(0);

  useEffect(() => {
    fetchBrands();
    fetchPrompts();
    fetchModels();
  }, []);

  const fetchTimeline = () => {
    if (!brandId) {
      setTimeline([]);
      setError(null);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    api.trends
      .get(parseInt(brandId), {
        model: modelFilter || undefined,
        prompt_id: promptFilter ? parseInt(promptFilter) : undefined,
      })
      .then((data) => {
        if (seq !== requestSeq.current) return;
        setTimeline(data);
        setError(null);
      })
      .catch((err: any) => {
        if (seq !== requestSeq.current) return;
        setError(err?.message || "Failed to load trend data");
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, modelFilter, promptFilter]);

  const allDimensions = Array.from(
    new Set(timeline.flatMap((e) => Object.keys(e.scores)))
  );

  const selectedBrandName = brands.find(
    (b) => String(b.id) === brandId
  )?.name;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="py-5 px-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="trend-brand">Brand</Label>
              <NativeSelect
                id="trend-brand"
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
              >
                <option value="">Select a brand...</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label htmlFor="trend-model">Model</Label>
              <NativeSelect
                id="trend-model"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
              >
                <option value="">All models</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label htmlFor="trend-prompt">Prompt</Label>
              <NativeSelect
                id="trend-prompt"
                value={promptFilter}
                onChange={(e) => setPromptFilter(e.target.value)}
              >
                <option value="">All prompts</option>
                {prompts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {!brandId ? (
        <EmptyState
          icon={TrendingUp}
          title="Select a brand"
          description="Choose a brand above to see how its scores change over time."
        />
      ) : loading ? (
        <Card>
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-10 bg-slate-100 rounded-md animate-subtle-pulse"
              />
            ))}
          </div>
        </Card>
      ) : error ? (
        <ErrorState message={error} onRetry={fetchTimeline} />
      ) : timeline.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title="No trend data"
          description="No comparisons found for this selection. Try adjusting your filters."
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Prompt</TableHead>
                {allDimensions.map((d) => (
                  <TableHead key={d} className="text-right">
                    {d}
                  </TableHead>
                ))}
                <TableHead className="text-center">Won</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timeline.map((entry, i) => {
                const won = entry.winner === selectedBrandName;
                return (
                  <TableRow key={i}>
                    <TableCell className="text-slate-500 whitespace-nowrap text-[13px]">
                      {entry.date}
                    </TableCell>
                    <TableCell>
                      <Badge variant="muted">
                        {formatModelLabel(entry.model)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[13px] text-slate-600">
                      {entry.prompt_name}
                    </TableCell>
                    {allDimensions.map((d) => (
                      <TableCell
                        key={d}
                        className="text-right text-[13px] font-semibold text-brand-950 tabular-nums"
                      >
                        {entry.scores[d] ?? (
                          <span className="text-slate-300">--</span>
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-center">
                      {won ? (
                        <span className={cn(
                          "inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold",
                          "bg-green-50 text-green-600"
                        )}>
                          Y
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-medium bg-slate-50 text-slate-400">
                          N
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-[11px] text-slate-400">
              {timeline.length} result{timeline.length !== 1 ? "s" : ""} found
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
